import StockPostingContextCard from "../../components/stock/StockPostingContextCard";
// src/pages/Sanitary/SanitaryDailySettlementPage.jsx
// =============================================================================
// Sanitary Documentation / Disinfection & Hygiene / Daily settlement
// =============================================================================

import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import AuthContext from "../../context/AuthContext";
import EvaluationPeriodPicker from "../../components/filters/EvaluationPeriodPicker";
import PaginationFooter from "../../components/common/PaginationFooter";
import useEvaluationPeriod from "../../hooks/useEvaluationPeriod";
import { EVALUATION_PERIOD_TYPES } from "../../utils/evaluationPeriodUtils";
import {
  adjustSanitaryConsumptionEntry,
  calculateSanitaryConsumption,
  confirmSanitaryConsumptionEntry,
  fetchSanitaryCalculationRuns,
  fetchSanitaryConsumptionEntries,
  fetchSanitaryConsumptionEntriesPage,
  historicalCatchUpSanitaryConsumption,
  postConfirmedSanitaryConsumptionEntries,
  postSanitaryConsumptionEntryToStock,
  rejectSanitaryConsumptionEntry,
} from "../../services/sanitarySettlementService";
import { createSanitarySolutionPreparation } from "../../services/sanitarySolutionService";

import "../Stock/StockMasterDataPage.css";
import "./SanitaryProductsPage.css";
import SanitaryPostingDiagnostics, { PostingFailure, postingFailureFromError } from "./components/SanitaryPostingDiagnostics";

const CALCULATION_MAX_DAYS = 62;
const DAY_MS = 24 * 60 * 60 * 1000;
const SETTLEMENT_RANGE_STORAGE_KEY = "hb_visits.sanitary.dailySettlement.range.v1";
const ENTRY_PAGE_SIZE_STORAGE_KEY = "hb_visits.sanitary.dailySettlement.pageSize.v1";
const PERIOD_MODES = [
  EVALUATION_PERIOD_TYPES.MONTH,
  EVALUATION_PERIOD_TYPES.THREE_MONTHS,
  EVALUATION_PERIOD_TYPES.SIX_MONTHS,
  EVALUATION_PERIOD_TYPES.YEAR,
  EVALUATION_PERIOD_TYPES.WEEK,
  EVALUATION_PERIOD_TYPES.DAY,
  EVALUATION_PERIOD_TYPES.MANUAL,
];

const localIsoDate = (value = new Date()) =>
  `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, "0")}-${String(value.getDate()).padStart(2, "0")}`;

function isIsoDate(value) {
  return /^\d{4}-\d{2}-\d{2}$/.test(String(value || ""));
}

function readStoredRange() {
  if (typeof window === "undefined" || !window.localStorage) return null;
  try {
    const stored = JSON.parse(window.localStorage.getItem(SETTLEMENT_RANGE_STORAGE_KEY) || "null");
    if (
      stored &&
      isIsoDate(stored.period_from) &&
      isIsoDate(stored.period_to) &&
      stored.period_from <= stored.period_to
    ) {
      return { period_from: stored.period_from, period_to: stored.period_to };
    }
  } catch (_) {
    // Local preference only. Invalid data falls back to the current month.
  }
  return null;
}

function persistSettlementRange(range) {
  if (typeof window === "undefined" || !window.localStorage) return;
  try {
    window.localStorage.setItem(SETTLEMENT_RANGE_STORAGE_KEY, JSON.stringify(range));
  } catch (_) {
    // Storage failure must not block sanitary work.
  }
}

function readPageSize() {
  if (typeof window === "undefined" || !window.localStorage) return 50;
  const value = Number(window.localStorage.getItem(ENTRY_PAGE_SIZE_STORAGE_KEY));
  return [25, 50, 100].includes(value) ? value : 50;
}

function persistPageSize(value) {
  if (typeof window === "undefined" || !window.localStorage) return;
  try {
    window.localStorage.setItem(ENTRY_PAGE_SIZE_STORAGE_KEY, String(value));
  } catch (_) {
    // Non-critical preference.
  }
}

function inclusiveDayCount(from, to) {
  if (!from || !to) return null;
  const start = Date.parse(`${from}T00:00:00Z`);
  const end = Date.parse(`${to}T00:00:00Z`);
  if (!Number.isFinite(start) || !Number.isFinite(end) || end < start) return null;
  return Math.floor((end - start) / DAY_MS) + 1;
}

function normalizeRole(role) {
  return String(role || "").trim().toLowerCase();
}

function resolveCurrentUserRole(auth) {
  return normalizeRole(
    auth?.authState?.user?.role ||
      auth?.user?.role ||
      auth?.role ||
      auth?.authState?.role
  );
}

function asArray(value) {
  return Array.isArray(value) ? value : [];
}

function formatQty(qty) {
  if (qty === undefined || qty === null || qty === "") return "—";
  const n = Number(qty);
  if (!Number.isFinite(n)) return String(qty);
  return n.toLocaleString(undefined, { maximumFractionDigits: 6 });
}

function formatDateTime(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString();
}

function runStatusLabel(t, status) {
  const key = String(status || "").trim().toLowerCase();
  return t(`sanitary:settlements.runs.statuses.${key}`, { defaultValue: status || "—" });
}

function productLabel(entry) {
  const p = entry?.product || entry?.rule?.product || {};
  const parts = [p.name_public, p.sku ? `SKU ${p.sku}` : null].filter(Boolean);
  return parts.join(" · ") || "—";
}

function locationLabel(entry) {
  const l = entry?.location || entry?.rule?.location || null;
  if (!l) return "—";
  return [l.code, l.name].filter(Boolean).join(" · ") || "—";
}

function ruleLabel(entry) {
  const r = entry?.rule || {};
  return [r.code, r.name].filter(Boolean).join(" · ") || `#${entry?.rule_id || "—"}`;
}

function statusClass(status) {
  const s = String(status || "").toLowerCase();
  if (["solution_prepared_and_posted", "stock_posted", "recorded_no_stock"].includes(s)) return "stock-md-pill";
  if (["awaiting_confirmation", "solution_ready_for_automatic_preparation", "solution_confirmed_waiting_for_preparation", "ready_for_automatic_posting", "confirmed_waiting_for_posting", "adjusted_waiting_for_posting"].includes(s)) return "stock-md-pill stock-md-pill--warning";
  if (s === "rejected" || s === "failed") return "stock-md-pill stock-md-pill--danger";
  return "stock-md-pill stock-md-pill--muted";
}

function sourceTypeLabel(t, type) {
  return t(`sanitary:settlements.sourceTypes.${type}`, { defaultValue: type || "—" });
}

function statusLabel(t, entry) {
  const code = entry?.business_status || entry?.status || "unknown";
  return t(`sanitary:settlements.businessStatuses.${code}`, {
    defaultValue: t(`sanitary:settlements.entryStatuses.${entry?.status}`, { defaultValue: code || "—" }),
  });
}

function entryKindLabel(t, entry) {
  const kind = entry?.entry_kind || "calculation";
  if (kind !== "correction_delta") {
    return t(`sanitary:settlements.entryKinds.${kind}`, {
      defaultValue: t("sanitary:settlements.entryKinds.calculation", { defaultValue: "Calculation" }),
    });
  }
  const direction = entry?.correction_direction || "neutral";
  return t(`sanitary:settlements.correctionDirections.${direction}`, { defaultValue: direction });
}

function technicalErrorDetail(err) {
  const status = err?.status ? `HTTP ${err.status}` : "";
  const code = err?.code ? String(err.code) : "";
  return [status, code].filter(Boolean).join(" / ");
}

function isWorkingSolutionEntry(entry) {
  return String(entry?.quantity_basis || entry?.ruleVersion?.quantity_basis || entry?.calculation_details_json?.quantity_basis || "direct_product") === "working_solution";
}

function isAutoWorkingSolutionEntry(entry) {
  return isWorkingSolutionEntry(entry) &&
    entry?.entry_kind !== "correction_delta" &&
    entry?.posts_to_stock &&
    entry?.requires_confirmation === false &&
    ["auto_confirmed", "confirmed", "adjusted"].includes(String(entry?.status || ""));
}

function workingSolutionDetails(entry) {
  return entry?.working_solution || entry?.calculation_details_json?.working_solution || null;
}

function localDateTimeInputForBusinessDate(businessDate) {
  const now = new Date();
  const localDate = localIsoDate(now);
  if (businessDate === localDate) {
    return `${localDate}T${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;
  }
  return `${businessDate}T12:00`;
}

function emptyEntryPage(pageSize = 50) {
  return {
    items: [],
    pagination: { page: 1, page_size: pageSize, total: 0, page_count: 1 },
    summary: {
      total: 0,
      awaiting: 0,
      auto_confirmed: 0,
      ready_to_post: 0,
      manual_solutions: 0,
      posted: 0,
      corrections: 0,
      errors: 0,
      attention: 0,
    },
    filters: { rules: [], products: [], statuses: [] },
  };
}

const EMPTY_ENTRY_FILTERS = {
  search: "",
  rule_id: "",
  product_id: "",
  entry_kind: "",
  status: "",
  attention_only: false,
  quick_filter: "all",
};

export default function SanitaryDailySettlementPage() {
  const { t } = useTranslation(["sanitary", "common"]);
  const auth = useContext(AuthContext);
  const role = resolveCurrentUserRole(auth);
  const canWrite = role === "superuser" || role === "reception";
  const canHistoricalCatchUp = role === "superuser";

  const initialStoredRange = useMemo(() => readStoredRange(), []);
  const period = useEvaluationPeriod({
    defaultMode: initialStoredRange ? EVALUATION_PERIOD_TYPES.MANUAL : EVALUATION_PERIOD_TYPES.MONTH,
    initialDate: initialStoredRange ? new Date(`${initialStoredRange.period_from}T12:00:00`) : new Date(),
    allowedModes: PERIOD_MODES,
  });
  const periodBootstrappedRef = useRef(false);
  const [periodReady, setPeriodReady] = useState(!initialStoredRange);
  const [appliedRange, setAppliedRange] = useState(() =>
    initialStoredRange || { period_from: period.from, period_to: period.to }
  );

  const [viewMode, setViewMode] = useState("period");
  const [runs, setRuns] = useState([]);
  const [selectedRun, setSelectedRun] = useState(null);
  const [historicalEntries, setHistoricalEntries] = useState([]);
  const [entryPageData, setEntryPageData] = useState(() => emptyEntryPage(readPageSize()));
  const [entryPage, setEntryPage] = useState(1);
  const [entryPageSize, setEntryPageSize] = useState(readPageSize);
  const [entryFilters, setEntryFilters] = useState(EMPTY_ENTRY_FILTERS);
  const [contextReady, setContextReady] = useState(false);

  const [loading, setLoading] = useState(false);
  const [entriesLoading, setEntriesLoading] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [catchingUp, setCatchingUp] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [hasLoaded, setHasLoaded] = useState(false);
  const [postingResult, setPostingResult] = useState(null);
  const [postingBusy, setPostingBusy] = useState(false);
  const postingInFlight = useRef(false);

  useEffect(() => {
    if (periodBootstrappedRef.current) return;
    periodBootstrappedRef.current = true;
    if (initialStoredRange) {
      period.setMode(EVALUATION_PERIOD_TYPES.MANUAL);
      period.setManualFrom(initialStoredRange.period_from);
      period.setManualTo(initialStoredRange.period_to);
      setAppliedRange(initialStoredRange);
      setPeriodReady(true);
    }
  }, [initialStoredRange, period]);

  useEffect(() => {
    if (!error) return undefined;
    const timer = window.setTimeout(() => setError(""), 8000);
    return () => window.clearTimeout(timer);
  }, [error]);

  useEffect(() => {
    if (!success) return undefined;
    const timer = window.setTimeout(() => setSuccess(""), 5000);
    return () => window.clearTimeout(timer);
  }, [success]);

  const currentPickerRange = useMemo(
    () => ({ period_from: period.from, period_to: period.to }),
    [period.from, period.to]
  );
  const calculationDays = useMemo(
    () => inclusiveDayCount(currentPickerRange.period_from, currentPickerRange.period_to),
    [currentPickerRange]
  );
  const calculationPeriodTooLong = Number.isFinite(calculationDays) && calculationDays > CALCULATION_MAX_DAYS;

  const summary = entryPageData.summary || {};
  const entries = asArray(entryPageData.items);
  const selectedRunReadOnly = Boolean(viewMode === "period" && selectedRun && String(selectedRun.status) !== "calculated");
  const canModifyEntries = canWrite && !selectedRunReadOnly;
  const pageInfo = entryPageData.pagination || { page: 1, page_size: entryPageSize, total: 0, page_count: 1 };
  const entryOptions = entryPageData.filters || { rules: [], products: [], statuses: [] };

  const visibleHistoricalEntries = useMemo(() =>
    asArray(historicalEntries)
      .filter((entry) => !selectedRun?.id || String(entry?.calculation_run_id || "") !== String(selectedRun.id)),
    [historicalEntries, selectedRun]
  );

  const loadEntriesForContext = async ({
    nextPage = entryPage,
    nextPageSize = entryPageSize,
    nextFilters = entryFilters,
    run = selectedRun,
    mode = viewMode,
    range = appliedRange,
  } = {}) => {
    setEntriesLoading(true);
    setError("");
    try {
      const params = mode === "open"
        ? {
            open_only: true,
            page: nextPage,
            page_size: nextPageSize,
          }
        : {
            period_from: range.period_from,
            period_to: range.period_to,
            calculation_run_id: run?.id || "",
            page: nextPage,
            page_size: nextPageSize,
          };

      const data = await fetchSanitaryConsumptionEntriesPage({
        ...params,
        search: nextFilters.search,
        rule_id: nextFilters.rule_id,
        product_id: nextFilters.product_id,
        entry_kind: nextFilters.entry_kind,
        status: nextFilters.status,
        attention_only: nextFilters.attention_only ? true : "",
        quick_filter: nextFilters.quick_filter !== "all" ? nextFilters.quick_filter : "",
      });
      setEntryPageData(data);
      setEntryPage(Number(data?.pagination?.page || nextPage || 1));
      setHasLoaded(true);
    } catch (err) {
      const detail = technicalErrorDetail(err);
      setError(
        detail
          ? t("sanitary:settlements.messages.loadFailedWithDetail", { detail })
          : t("sanitary:settlements.messages.loadFailed")
      );
    } finally {
      setEntriesLoading(false);
    }
  };

  const loadPeriodContext = async (range = currentPickerRange, options = {}) => {
    setLoading(true);
    setError("");
    setSuccess("");
    setContextReady(false);
    try {
      const [runRows, historyRows] = await Promise.all([
        fetchSanitaryCalculationRuns({
          period_from: range.period_from,
          period_to: range.period_to,
          exact_period: true,
          limit: 100,
        }),
        fetchSanitaryConsumptionEntries({
          period_from: range.period_from,
          period_to: range.period_to,
          history_only: true,
          limit: 1000,
        }),
      ]);
      const nextRuns = asArray(runRows);
      const preferredId = options.selectedRunId ? String(options.selectedRunId) : "";
      const nextSelected =
        nextRuns.find((run) => preferredId && String(run.id) === preferredId) ||
        nextRuns.find((run) => String(run.status) === "calculated") ||
        nextRuns[0] ||
        null;

      setAppliedRange(range);
      persistSettlementRange(range);
      setViewMode("period");
      setRuns(nextRuns);
      setSelectedRun(nextSelected);
      setHistoricalEntries(asArray(historyRows));
      setEntryPage(1);
      setEntryFilters(EMPTY_ENTRY_FILTERS);
      setEntryPageData(emptyEntryPage(entryPageSize));
      setContextReady(true);
      setHasLoaded(true);
    } catch (err) {
      const detail = technicalErrorDetail(err);
      setError(
        detail
          ? t("sanitary:settlements.messages.loadFailedWithDetail", { detail })
          : t("sanitary:settlements.messages.loadFailed")
      );
    } finally {
      setLoading(false);
    }
  };

  const loadOpenContext = async () => {
    setLoading(true);
    setError("");
    setSuccess("");
    setContextReady(false);
    try {
      const runRows = await fetchSanitaryCalculationRuns({ limit: 5 });
      setRuns(asArray(runRows));
      setSelectedRun(null);
      setHistoricalEntries([]);
      setViewMode("open");
      setEntryPage(1);
      setEntryFilters(EMPTY_ENTRY_FILTERS);
      setEntryPageData(emptyEntryPage(entryPageSize));
      setContextReady(true);
      setHasLoaded(true);
    } catch (err) {
      setError(t("sanitary:settlements.messages.loadFailed"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!periodReady) return;
    loadPeriodContext(initialStoredRange || currentPickerRange);
  }, [periodReady]);

  useEffect(() => {
    if (!contextReady) return undefined;
    if (viewMode === "period" && !selectedRun?.id) {
      setEntryPageData(emptyEntryPage(entryPageSize));
      return undefined;
    }
    const timer = window.setTimeout(() => {
      loadEntriesForContext();
    }, entryFilters.search ? 250 : 0);
    return () => window.clearTimeout(timer);
  }, [
    contextReady,
    viewMode,
    selectedRun?.id,
    entryPage,
    entryPageSize,
    entryFilters.search,
    entryFilters.rule_id,
    entryFilters.product_id,
    entryFilters.entry_kind,
    entryFilters.status,
    entryFilters.attention_only,
    entryFilters.quick_filter,
    appliedRange.period_from,
    appliedRange.period_to,
  ]);

  const refreshCurrentView = async () => {
    if (viewMode === "open") {
      await loadOpenContext();
      return;
    }
    await loadPeriodContext(currentPickerRange, { selectedRunId: selectedRun?.id });
  };

  const setPickerToRange = (range) => {
    period.setMode(EVALUATION_PERIOD_TYPES.MANUAL);
    period.setManualFrom(range.period_from);
    period.setManualTo(range.period_to);
  };

  const handleSelectRun = async (run) => {
    if (!run?.id) return;
    setError("");
    if (viewMode === "open" || run.period_from !== appliedRange.period_from || run.period_to !== appliedRange.period_to) {
      const range = { period_from: run.period_from, period_to: run.period_to };
      setPickerToRange(range);
      await loadPeriodContext(range, { selectedRunId: run.id });
      return;
    }
    setSelectedRun(run);
    setEntryPage(1);
  };

  const handleRunKeyDown = (event, run) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleSelectRun(run);
    }
  };

  const patchEntryFilter = (patch) => {
    setEntryPage(1);
    setEntryFilters((prev) => ({ ...prev, ...patch }));
  };

  const clearEntryFilters = () => {
    setEntryPage(1);
    setEntryFilters(EMPTY_ENTRY_FILTERS);
  };

  const handlePageSizeChange = (nextSize) => {
    persistPageSize(nextSize);
    setEntryPageSize(nextSize);
    setEntryPage(1);
  };

  const reloadAfterEntryAction = async () => {
    await loadEntriesForContext({ nextPage: entryPage });
  };

  const loadRunAfterCalculation = async (result) => {
    const run = result?.run || null;
    await loadPeriodContext(currentPickerRange, { selectedRunId: run?.id || null });
  };

  const handleCalculate = async () => {
    setError("");
    setSuccess("");
    if (calculationPeriodTooLong) {
      setError(t("sanitary:settlements.messages.periodTooLong", { max: CALCULATION_MAX_DAYS, days: calculationDays }));
      return;
    }

    setCalculating(true);
    try {
      const result = await calculateSanitaryConsumption({
        period_from: currentPickerRange.period_from,
        period_to: currentPickerRange.period_to,
        trigger_type: "manual",
        mode: "normal",
      });
      setSuccess(t("sanitary:settlements.messages.calculated"));
      await loadRunAfterCalculation(result);
    } catch (err) {
      if (err?.code === "POSTED_ENTRIES_EXIST" || err?.data?.requiresCorrectionMode) {
        const postedEntriesCount = err?.data?.details?.postedEntriesCount || 0;
        const confirmed = window.confirm(t("sanitary:settlements.prompts.correctionRecalculation", { postedEntriesCount }));
        if (!confirmed) {
          setError(t("sanitary:settlements.messages.correctionRecalculationCancelled"));
          setCalculating(false);
          return;
        }
        try {
          const correctionResult = await calculateSanitaryConsumption({
            period_from: currentPickerRange.period_from,
            period_to: currentPickerRange.period_to,
            trigger_type: "manual",
            mode: "correction_delta",
          });
          const noDifferences = Boolean(correctionResult?.summary?.no_differences);
          setSuccess(
            noDifferences
              ? t("sanitary:settlements.messages.correctionNoDifferences")
              : t("sanitary:settlements.messages.correctionCalculated")
          );
          await loadRunAfterCalculation(correctionResult);
        } catch (_) {
          setError(t("sanitary:settlements.messages.correctionCalculateFailed"));
        }
      } else if (err?.code === "PERIOD_TOO_LONG") {
        setError(t("sanitary:settlements.messages.periodTooLong", {
          max: err?.data?.details?.max_days || CALCULATION_MAX_DAYS,
          days: err?.data?.details?.requested_days || calculationDays || "—",
        }));
      } else {
        setError(t("sanitary:settlements.messages.calculateFailed"));
      }
    } finally {
      setCalculating(false);
    }
  };

  const handleConfirmEntry = async (entry) => {
    setError(""); setSuccess("");
    try {
      await confirmSanitaryConsumptionEntry(entry.id, {
        actual_quantity: entry.actual_quantity || entry.planned_quantity,
        actual_unit_code: entry.actual_unit_code || entry.planned_unit_code,
      });
      setSuccess(t("sanitary:settlements.messages.confirmed"));
      await reloadAfterEntryAction();
    } catch (_) { setError(t("sanitary:settlements.messages.confirmFailed")); }
  };

  const handleAdjustEntry = async (entry) => {
    const qty = window.prompt(t("sanitary:settlements.prompts.adjustQuantity"), String(entry.actual_quantity || entry.planned_quantity || ""));
    if (qty === null) return;
    const comment = window.prompt(t("sanitary:settlements.prompts.adjustComment"));
    if (comment === null) return;
    setError(""); setSuccess("");
    try {
      await adjustSanitaryConsumptionEntry(entry.id, {
        actual_quantity: qty,
        actual_unit_code: entry.actual_unit_code || entry.planned_unit_code,
        comment,
      });
      setSuccess(t("sanitary:settlements.messages.adjusted"));
      await reloadAfterEntryAction();
    } catch (_) { setError(t("sanitary:settlements.messages.adjustFailed")); }
  };

  const handleRejectEntry = async (entry) => {
    const comment = window.prompt(t("sanitary:settlements.prompts.rejectComment"));
    if (comment === null) return;
    setError(""); setSuccess("");
    try {
      await rejectSanitaryConsumptionEntry(entry.id, { comment });
      setSuccess(t("sanitary:settlements.messages.rejected"));
      await reloadAfterEntryAction();
    } catch (_) { setError(t("sanitary:settlements.messages.rejectFailed")); }
  };

  // Separate posting from view refresh: a failed refresh must NOT be reported
  // as a failed stock transaction after the API has already committed it.
  const refreshAfterPosting = async () => {
    try { await reloadAfterEntryAction(); }
    catch (_) { setError(t("sanitary:postingDiagnostics.refreshFailed")); }
  };
  const postSingleEntry = async (entry, solution = false) => {
    if (postingInFlight.current) return;
    postingInFlight.current = true; setPostingBusy(true); setError(""); setSuccess("");
    try {
      if (solution) {
        await createSanitarySolutionPreparation({ consumption_entry_id: entry.id,
          prepared_at: localDateTimeInputForBusinessDate(entry.business_date), batch_policy: "FEFO",
          automatic: isAutoWorkingSolutionEntry(entry) });
      } else await postSanitaryConsumptionEntryToStock(entry.id, { batch_policy: "FEFO" });
      setPostingResult({ requested: 1, posted: 1, failed: 0, results: [] });
    } catch (err) {
      setPostingResult({ requested: 1, posted: 0, failed: 1, results: [postingFailureFromError(err, entry)] });
    } finally {
      await refreshAfterPosting();
      postingInFlight.current = false; setPostingBusy(false);
    }
  };
  const handlePostEntry = (entry) => postSingleEntry(entry, false);
  const handlePrepareSolution = (entry) => postSingleEntry(entry, true);
  const handlePostConfirmed = async () => {
    if (postingInFlight.current) return;
    if (!selectedRun?.id || String(selectedRun.status) !== "calculated") {
      setError(t("sanitary:settlements.messages.bulkPostRunRequired")); return;
    }
    if (!window.confirm(t("sanitary:postingDiagnostics.bulkConfirm", { id: selectedRun.id,
      from: selectedRun.period_from, to: selectedRun.period_to }))) return;
    postingInFlight.current = true; setPostingBusy(true); setError(""); setSuccess("");
    const runId = selectedRun.id;
    try {
      const result = await postConfirmedSanitaryConsumptionEntries({ period_from: selectedRun.period_from,
        period_to: selectedRun.period_to, calculation_run_id: runId, batch_policy: "FEFO" });
      setPostingResult({ ...result, calculation_run_id: runId });
    } catch (err) {
      // Non-200 failure of the request itself. Per-entry errors are in results.
      const failure = postingFailureFromError(err, {});
      setPostingResult({ requested: 0, posted: 0, failed: 1,
        calculation_run_id: runId, results: [{ ...failure, id: "request" }] });
    } finally {
      await refreshAfterPosting();
      postingInFlight.current = false; setPostingBusy(false);
    }
  };

  const handleHistoricalCatchUp = async () => {
    const confirmed = window.confirm(t("sanitary:settlements.prompts.historicalCatchUp", {
      from: currentPickerRange.period_from,
      to: currentPickerRange.period_to,
    }));
    if (!confirmed) return;

    setCatchingUp(true); setError(""); setSuccess("");
    try {
      const result = await historicalCatchUpSanitaryConsumption({
        period_from: currentPickerRange.period_from,
        period_to: currentPickerRange.period_to,
        batch_policy: "FEFO",
      });
      if (result?.completed) {
        setSuccess(t("sanitary:settlements.messages.historicalCatchUpCompleted", {
          days: result?.days_processed || 0,
          posted: result?.posted_entries || 0,
        }));
      } else {
        const paused = asArray(result?.results).find((row) => row?.date === result?.paused_date);
        setError(t("sanitary:settlements.messages.historicalCatchUpPaused", {
          date: result?.paused_date || "—",
          reason: paused?.error_message || paused?.error_code || "—",
        }));
      }
      await loadPeriodContext(currentPickerRange);
    } catch (err) {
      const detail = technicalErrorDetail(err);
      setError(detail
        ? t("sanitary:settlements.messages.historicalCatchUpFailedWithDetail", { detail })
        : t("sanitary:settlements.messages.historicalCatchUpFailed"));
    } finally { setCatchingUp(false); }
  };

  const paginationSummary = useMemo(() => {
    const total = Number(pageInfo.total || 0);
    if (!total) return t("sanitary:settlements.pagination.empty", { defaultValue: "Brak pozycji" });
    const from = (Number(pageInfo.page || 1) - 1) * Number(pageInfo.page_size || entryPageSize) + 1;
    const to = Math.min(total, from + Number(pageInfo.page_size || entryPageSize) - 1);
    return t("sanitary:settlements.pagination.summary", {
      from,
      to,
      total,
      defaultValue: `Pozycje ${from}–${to} z ${total}`,
    });
  }, [pageInfo, entryPageSize, t]);

  const quickFilters = [
    ["all", "all", summary.total || 0],
    ["awaiting", "awaiting", summary.awaiting || 0],
    ["ready", "ready", summary.ready_to_post || 0],
    ["manual_solutions", "manualSolutions", summary.manual_solutions || 0],
    ["corrections", "corrections", summary.corrections || 0],
    ["errors", "errors", summary.errors || 0],
    ["posted", "posted", summary.posted || 0],
  ];

  return (
    <div className="stock-md-page sanitary-page">
      <div className="stock-md-header">
        <div>
          <div className="stock-md-eyebrow">{t("sanitary:settlements.eyebrow")}</div>
          <h1>{t("sanitary:settlements.title")}</h1>
          <p>{t("sanitary:settlements.subtitle")}</p>
        </div>
      </div>

      <div className="stock-md-info-box">{t("sanitary:settlements.guidance")}</div>
      {error && <div className="stock-md-error">{error}</div>}
      {success && <div className="stock-md-success">{success}</div>}
      {!canWrite && <div className="stock-md-info-box">{t("sanitary:settlements.messages.readOnly")}</div>}

      <section className="stock-md-card">
        <div className="stock-md-card-header">
          <div>
            <h2>{t("sanitary:settlements.filters.title")}</h2>
            <p>{t("sanitary:settlements.filters.subtitle")}</p>
          </div>
        </div>

        <div className="sanitary-period-toolbar">
          <EvaluationPeriodPicker
            period={period}
            disabled={viewMode === "open" || loading}
            modeLabel={t("sanitary:common.period.mode", { defaultValue: "Typ okresu" })}
            fromLabel={t("sanitary:common.period.from", { defaultValue: "Od" })}
            toLabel={t("sanitary:common.period.to", { defaultValue: "Do" })}
            displayLabel={t("sanitary:common.period.selected", { defaultValue: "Wybrany okres" })}
          />
          <div className="sanitary-period-toolbar__actions">
            <button type="button" className="stock-md-button stock-md-button--secondary" disabled={loading} onClick={refreshCurrentView}>
              {t("sanitary:settlements.actions.refresh")}
            </button>
            <button
              type="button"
              className="stock-md-button"
              disabled={!canWrite || postingBusy || calculating || viewMode === "open" || calculationPeriodTooLong || !currentPickerRange.period_from || !currentPickerRange.period_to}
              onClick={handleCalculate}
              title={calculationPeriodTooLong
                ? t("sanitary:settlements.messages.periodTooLong", { max: CALCULATION_MAX_DAYS, days: calculationDays })
                : t("sanitary:settlements.actions.calculateHint", { max: CALCULATION_MAX_DAYS })}
            >
              {calculating ? t("sanitary:settlements.messages.calculating") : t("sanitary:settlements.actions.calculate")}
            </button>
            <button
              type="button"
              className="stock-md-button stock-md-button--secondary"
              disabled={!canHistoricalCatchUp || postingBusy || catchingUp || calculating || loading || viewMode === "open"}
              onClick={handleHistoricalCatchUp}
            >
              {catchingUp ? t("sanitary:settlements.messages.historicalCatchUpRunning") : t("sanitary:settlements.actions.historicalCatchUp")}
            </button>
            <button
              type="button"
              className="stock-md-button stock-md-button--secondary"
              disabled={!canWrite || postingBusy || entriesLoading || viewMode === "open" || selectedRunReadOnly || Number(summary.ready_to_post || 0) <= 0 || !selectedRun?.id}
              onClick={handlePostConfirmed}
            >
              {t("sanitary:settlements.actions.postConfirmed")}
            </button>
            <button
              type="button"
              className={`stock-md-button stock-md-button--small ${viewMode === "open" ? "" : "stock-md-button--secondary"}`}
              disabled={loading}
              onClick={loadOpenContext}
            >
              {t("sanitary:settlements.views.open")}
            </button>
          </div>
        </div>
        <SanitaryPostingDiagnostics result={postingResult} onDismiss={() => setPostingResult(null)} />
        {viewMode === "open" && <div className="stock-md-info-box">{t("sanitary:settlements.views.openHint")}</div>}
      </section>

      <div className="stock-md-grid stock-md-grid--wide stock-md-grid--top">
        <section className="stock-md-card">
          <div className="stock-md-card-header">
            <div>
              <h2>{viewMode === "period" ? t("sanitary:settlements.runs.periodTitle", { defaultValue: "RUN-y dla wybranego okresu" }) : t("sanitary:settlements.runs.recentTitle", { defaultValue: "Ostatnie uruchomienia naliczenia" })}</h2>
              <p>{viewMode === "period" ? t("sanitary:settlements.runs.periodSubtitle", { defaultValue: "Kliknij dowolny wiersz RUN-u, aby poniżej zobaczyć jego pozycje." }) : t("sanitary:settlements.runs.subtitle")}</p>
            </div>
          </div>

          <div className="stock-md-table-wrap">
            <table className="stock-md-table sanitary-run-table">
              <thead><tr>
                <th>{t("sanitary:settlements.runs.table.run")}</th>
                <th>{t("sanitary:settlements.runs.table.period")}</th>
                <th>{t("sanitary:settlements.runs.table.status")}</th>
                <th>{t("sanitary:settlements.runs.table.summary")}</th>
                <th>{t("sanitary:settlements.runs.table.created")}</th>
              </tr></thead>
              <tbody>
                {runs.length === 0 ? <tr><td className="stock-md-empty" colSpan="5">{loading ? t("sanitary:settlements.messages.loading") : t("sanitary:settlements.runs.empty")}</td></tr> : runs.map((run) => {
                  const s = run.summary_json || {};
                  const selected = String(selectedRun?.id || "") === String(run.id);
                  return (
                    <tr
                      key={run.id}
                      className={`${selected ? "stock-md-row-selected " : ""}sanitary-run-row`.trim()}
                      onClick={() => handleSelectRun(run)}
                      onKeyDown={(event) => handleRunKeyDown(event, run)}
                      tabIndex={0}
                      role="button"
                      aria-selected={selected}
                    >
                      <td>#{run.id}</td>
                      <td>{run.period_from} — {run.period_to}</td>
                      <td><span className="stock-md-pill stock-md-pill--muted">{runStatusLabel(t, run.status)}</span></td>
                      <td>{t("sanitary:settlements.runs.summaryText", { entries: s.entries ?? 0, awaiting: s.awaiting_confirmation ?? 0, auto: s.auto_confirmed ?? 0 })}</td>
                      <td>{formatDateTime(run.createdAt)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>

        <section className="stock-md-card">
          <div className="stock-md-card-header"><div>
            <h2>{t("sanitary:settlements.details.title")}</h2>
            <p>{selectedRun ? t("sanitary:settlements.details.selected", { id: selectedRun.id }) : t("sanitary:settlements.details.selectRun")}</p>
          </div></div>
          {selectedRun ? <div className="stock-md-selected-box">
            <strong>{t("sanitary:settlements.details.period")}</strong><span>{selectedRun.period_from} — {selectedRun.period_to}</span>
            <strong>{t("sanitary:settlements.details.status")}</strong><span>{runStatusLabel(t, selectedRun.status)}</span>
            <strong>{t("sanitary:settlements.details.summary")}</strong><span>{t("sanitary:settlements.runs.summaryText", {
              entries: selectedRun.summary_json?.entries ?? summary.total ?? 0,
              awaiting: selectedRun.summary_json?.awaiting_confirmation ?? summary.awaiting ?? 0,
              auto: selectedRun.summary_json?.auto_confirmed ?? summary.auto_confirmed ?? 0,
            })}</span>
          </div> : <div className="stock-md-empty">{t("sanitary:settlements.details.noSelection")}</div>}
        </section>
      </div>

      <section className="stock-md-card">
        <div className="stock-md-card-header"><div>
          <h2>{viewMode === "open" ? t("sanitary:settlements.entries.openTitle") : t("sanitary:settlements.entries.title")}</h2>
          <p>{viewMode === "open" ? t("sanitary:settlements.entries.openSubtitle") : t("sanitary:settlements.entries.subtitle")}</p>
        </div></div>

        {selectedRunReadOnly && (
          <div className="stock-md-info-box" style={{ marginBottom: "0.75rem" }}>
            {t("sanitary:settlements.messages.supersededRunReadOnly", { defaultValue: "Wybrany RUN jest zastąpiony i pozostaje tylko do odczytu. Operacje wykonuj na aktualnym RUN-ie oznaczonym jako Naliczone." })}
          </div>
        )}

        <div className="sanitary-entry-quick-filters" role="group" aria-label={t("sanitary:settlements.filters.quickFilters", { defaultValue: "Szybkie filtry" })}>
          {quickFilters.map(([value, labelKey, count]) => (
            <button
              key={value}
              type="button"
              className={`sanitary-stat-filter ${entryFilters.quick_filter === value ? "sanitary-stat-filter--active" : ""}`}
              onClick={() => patchEntryFilter({ quick_filter: value })}
              disabled={entriesLoading}
            >
              {t(`sanitary:settlements.quickFilters.${labelKey}`, { defaultValue: labelKey })}: <strong>{count}</strong>
            </button>
          ))}
        </div>

        <div className="sanitary-entry-filters">
          <label className="stock-md-field stock-md-field--grow">
            <span>{t("sanitary:settlements.filters.searchEntries", { defaultValue: "Szukaj w pozycjach" })}</span>
            <input value={entryFilters.search} onChange={(e) => patchEntryFilter({ search: e.target.value })} placeholder={t("sanitary:settlements.filters.searchEntriesPlaceholder", { defaultValue: "Reguła, produkt, SKU, źródło..." })} />
          </label>
          <label className="stock-md-field">
            <span>{t("sanitary:settlements.filters.rule", { defaultValue: "Reguła" })}</span>
            <select value={entryFilters.rule_id} onChange={(e) => patchEntryFilter({ rule_id: e.target.value })}>
              <option value="">{t("sanitary:common.all")}</option>
              {asArray(entryOptions.rules).map((row) => <option key={row.id} value={row.id}>{[row.code, row.name].filter(Boolean).join(" · ")}</option>)}
            </select>
          </label>
          <label className="stock-md-field">
            <span>{t("sanitary:settlements.filters.product", { defaultValue: "Produkt" })}</span>
            <select value={entryFilters.product_id} onChange={(e) => patchEntryFilter({ product_id: e.target.value })}>
              <option value="">{t("sanitary:common.all")}</option>
              {asArray(entryOptions.products).map((row) => <option key={row.id} value={row.id}>{[row.name_public, row.sku].filter(Boolean).join(" · ")}</option>)}
            </select>
          </label>
          <label className="stock-md-field">
            <span>{t("sanitary:settlements.filters.kind", { defaultValue: "Typ" })}</span>
            <select value={entryFilters.entry_kind} onChange={(e) => patchEntryFilter({ entry_kind: e.target.value })}>
              <option value="">{t("sanitary:common.all")}</option>
              {['calculation', 'correction_delta', 'manual_correction'].map((kind) => <option key={kind} value={kind}>{t(`sanitary:settlements.entryKinds.${kind}`, { defaultValue: kind })}</option>)}
            </select>
          </label>
          <label className="stock-md-field">
            <span>{t("sanitary:settlements.filters.status", { defaultValue: "Status" })}</span>
            <select value={entryFilters.status} onChange={(e) => patchEntryFilter({ status: e.target.value })}>
              <option value="">{t("sanitary:common.all")}</option>
              {asArray(entryOptions.statuses).map((status) => <option key={status} value={status}>{t(`sanitary:settlements.entryStatuses.${status}`, { defaultValue: status })}</option>)}
            </select>
          </label>
          <label className="stock-md-checkbox sanitary-entry-attention">
            <input type="checkbox" checked={entryFilters.attention_only} onChange={(e) => patchEntryFilter({ attention_only: e.target.checked })} />
            <span>{t("sanitary:settlements.filters.attentionOnly", { defaultValue: "Tylko wymagające uwagi" })}</span>
          </label>
          <button type="button" className="stock-md-button stock-md-button--secondary" onClick={clearEntryFilters} disabled={entriesLoading}>
            {t("sanitary:settlements.actions.clearFilters", { defaultValue: "Wyczyść filtry" })}
          </button>
        </div>

        <div className="stock-md-table-wrap">
          <table className="stock-md-table stock-md-table--compact">
            <thead><tr>
              <th>{t("sanitary:settlements.entries.table.date")}</th><th>{t("sanitary:settlements.entries.table.rule")}</th><th>{t("sanitary:settlements.entries.table.source")}</th><th>{t("sanitary:settlements.entries.table.kind")}</th><th>{t("sanitary:settlements.entries.table.product")}</th><th>{t("sanitary:settlements.entries.table.location")}</th><th>{t("sanitary:settlements.entries.table.planned")}</th><th>{t("sanitary:settlements.entries.table.actual")}</th><th>{t("sanitary:settlements.entries.table.status")}</th><th>{t("sanitary:settlements.entries.table.stock")}</th><th>{t("sanitary:settlements.entries.table.actions")}</th>
            </tr></thead>
            <tbody>
              {entries.length === 0 ? <tr><td className="stock-md-empty" colSpan="11">{entriesLoading ? t("sanitary:settlements.messages.loading") : t("sanitary:settlements.entries.empty")}</td></tr> : entries.map((entry) => (
                <tr key={entry.id}>
                  <td>{entry.business_date}</td>
                  <td>{ruleLabel(entry)}</td>
                  <td><strong>{sourceTypeLabel(t, entry.source_type)}</strong><div className="stock-md-muted">{entry.source_label || "—"}</div></td>
                  <td>{entryKindLabel(t, entry)}{entry?.entry_kind === "correction_delta" && entry?.base_entry_id && <div className="stock-md-muted">{t("sanitary:settlements.entries.baseEntry", { id: entry.base_entry_id })}</div>}</td>
                  <td>{productLabel(entry)}</td>
                  <td>{locationLabel(entry)}</td>
                  <td><div>{formatQty(entry.planned_quantity)} {entry.planned_unit_symbol || entry.planned_unit_code}</div>{isWorkingSolutionEntry(entry) && workingSolutionDetails(entry) ? <div className="stock-md-muted">{t("sanitary:settlements.entries.workingSolutionExpected", { concentration: formatQty(workingSolutionDetails(entry)?.concentration_percent), concentrate: `${formatQty(workingSolutionDetails(entry)?.expected_concentrate_quantity)} ${workingSolutionDetails(entry)?.expected_concentrate_unit_code || ""}` })}</div> : null}</td>
                  <td>{entry.actual_quantity == null ? "—" : `${formatQty(entry.actual_quantity)} ${entry.actual_unit_symbol || entry.actual_unit_code}`}</td>
                  <td><span className={statusClass(entry.business_status || entry.status)}>{statusLabel(t, entry)}</span></td>
                  <td>{entry.posting_error_code && !entry.stock_movement_id ? <PostingFailure failure={entry} compact /> : entry.solutionPreparation ? t("sanitary:settlements.entries.solutionPreparedStock", { preparation: entry.solutionPreparation.id, movement: entry.solutionPreparation.stock_movement_id || entry.stock_movement_id || "—" }) : entry.stock_movement_id ? t("sanitary:settlements.entries.posted", { id: entry.stock_movement_id }) : entry.posts_to_stock && isWorkingSolutionEntry(entry) && isAutoWorkingSolutionEntry(entry) ? t("sanitary:settlements.entries.solutionAutomaticReady") : entry.posts_to_stock && isWorkingSolutionEntry(entry) ? t("sanitary:settlements.entries.solutionPreparationRequired") : entry.posts_to_stock ? t("sanitary:settlements.entries.postsLater") : t("sanitary:settlements.entries.noStock")}{entry.stock_movement_id && <StockPostingContextCard snapshot={entry.stock_posting_result_json} />}</td>
                  <td><div className="stock-md-actions-inline">
                    {canModifyEntries && !entry.stock_movement_id && entry.posting_error_code && isAutoWorkingSolutionEntry(entry) && ["auto_confirmed", "confirmed", "adjusted"].includes(entry.status) && <button type="button" className="stock-md-button stock-md-button--small" disabled={postingBusy} onClick={() => handlePrepareSolution(entry)}>{t("sanitary:postingDiagnostics.retry")}</button>}
                    {canModifyEntries && entry.status === "awaiting_confirmation" && <button type="button" className="stock-md-button stock-md-button--small" disabled={postingBusy} onClick={() => handleConfirmEntry(entry)}>{t("sanitary:settlements.actions.confirm")}</button>}
                    {canModifyEntries && !entry.stock_movement_id && entry.status !== "rejected" && entry.status !== "posted_to_inventory" && <button type="button" className="stock-md-button stock-md-button--small stock-md-button--secondary" disabled={postingBusy} onClick={() => handleAdjustEntry(entry)}>{t("sanitary:settlements.actions.adjust")}</button>}
                    {canModifyEntries && !entry.stock_movement_id && entry.status !== "rejected" && entry.status !== "posted_to_inventory" && <button type="button" className="stock-md-button stock-md-button--small stock-md-button--danger" disabled={postingBusy} onClick={() => handleRejectEntry(entry)}>{t("sanitary:settlements.actions.reject")}</button>}
                    {canModifyEntries && entry.posts_to_stock && !entry.stock_movement_id && !isWorkingSolutionEntry(entry) && ["auto_confirmed", "confirmed", "adjusted"].includes(entry.status) && <button type="button" className="stock-md-button stock-md-button--small" disabled={postingBusy} onClick={() => handlePostEntry(entry)}>{entry.posting_error_code ? t("sanitary:postingDiagnostics.retry") : t("sanitary:settlements.actions.postToStock")}</button>}
                    {canModifyEntries && entry.posts_to_stock && !entry.stock_movement_id && isWorkingSolutionEntry(entry) && ["auto_confirmed", "confirmed", "adjusted"].includes(entry.status) && !entry.solutionPreparation && entry.entry_kind !== "correction_delta" && !isAutoWorkingSolutionEntry(entry) && <button type="button" className="stock-md-button stock-md-button--small stock-md-button--primary" disabled={postingBusy} onClick={() => handlePrepareSolution(entry)}>{entry.posting_error_code ? t("sanitary:postingDiagnostics.retry") : t("sanitary:settlements.actions.prepareAndPostSolution")}</button>}
                  </div></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <PaginationFooter
          className="sanitary-settlement-pagination"
          page={Number(pageInfo.page || 1)}
          pageCount={Number(pageInfo.page_count || 1)}
          pageSize={Number(pageInfo.page_size || entryPageSize)}
          pageSizeOptions={[25, 50, 100]}
          totalRows={Number(pageInfo.total || 0)}
          disabled={entriesLoading}
          onPageSizeChange={handlePageSizeChange}
          onPrev={() => setEntryPage((prev) => Math.max(1, prev - 1))}
          onNext={() => setEntryPage((prev) => Math.min(Number(pageInfo.page_count || 1), prev + 1))}
          summary={<span>{paginationSummary}</span>}
        />
      </section>

      {viewMode === "period" && visibleHistoricalEntries.length > 0 && <section className="stock-md-card">
        <div className="stock-md-card-header"><div><h2>{t("sanitary:settlements.history.title")}</h2><p>{t("sanitary:settlements.history.subtitle")}</p></div></div>
        <div className="stock-md-info-box" style={{ marginBottom: "0.75rem" }}>{t("sanitary:settlements.history.guidance")}</div>
        <div className="stock-md-table-wrap"><table className="stock-md-table stock-md-table--compact"><thead><tr>
          <th>{t("sanitary:settlements.history.table.date")}</th><th>{t("sanitary:settlements.history.table.rule")}</th><th>{t("sanitary:settlements.history.table.product")}</th><th>{t("sanitary:settlements.history.table.run")}</th><th>{t("sanitary:settlements.history.table.movement")}</th><th>{t("sanitary:settlements.history.table.relation")}</th>
        </tr></thead><tbody>{visibleHistoricalEntries.map((entry) => <tr key={`history-${entry.id}`}>
          <td>{entry.business_date}</td><td>{ruleLabel(entry)}</td><td>{productLabel(entry)}</td><td>{entry.calculationRun?.id ? `#${entry.calculationRun.id}` : `#${entry.calculation_run_id || "—"}`}<div className="stock-md-muted">{t("sanitary:settlements.runs.statuses.superseded")}</div></td><td>{entry.stock_movement_id ? t("sanitary:settlements.entries.posted", { id: entry.stock_movement_id }) : t("sanitary:settlements.history.postedWithoutMovementId")}</td><td><span className={entry.current_occurrence_exists ? "stock-md-pill stock-md-pill--muted" : "stock-md-pill stock-md-pill--warning"}>{entry.current_occurrence_exists ? t("sanitary:settlements.history.relations.activeOccurrence") : t("sanitary:settlements.history.relations.outsideActiveRun")}</span></td>
        </tr>)}</tbody></table></div>
      </section>}

      {!loading && !entriesLoading && !error && hasLoaded && Number(pageInfo.total || 0) === 0 && <div className="stock-md-info-box">
        {viewMode === "open" ? t("sanitary:settlements.messages.openEmptyState") : selectedRun ? t("sanitary:settlements.messages.emptyState") : t("sanitary:settlements.runs.empty")}
      </div>}
    </div>
  );
}
